import * as React from 'react'
import moment from 'moment'

import { IEpiProps } from '../models/epi-props';
import { ISearchFilter, FilterParameterTypeString } from '../../global/models/search-context';
import { ISearchField } from '../models/search-field';
import { DropdownSelect } from '../../global/components/inputs/selects/dropdown-select';

interface IProps {
    name: string
    epiProps: IEpiProps,
    searchField: ISearchField,
    searchFilters: ISearchFilter[]

    // Callback for when the search field is changed
    onChange: (searchField: ISearchField) => void;
    onKeyDown: (e: any) => void;
}

/**
 * The initial values for the search fields
 */
const InitialValues = {
    /** Empty string for text filters */
    Text: '',
    /** Split query for date filters */
    Date: '-',
    /** Split query with 1 as first value for number filters */
    Number: '1-'
}

export class SearchFilter extends React.Component<IProps> {

    private initialInput: HTMLInputElement;

    componentDidMount() {
        if (this.initialInput) {
            this.initialInput.focus();
        }
    }

    render() {
        const { searchFilters } = this.props;

        return (
            <React.Fragment>
                <DropdownSelect
                    name={`${this.props.name}-filter`}
                    values={searchFilters.map(this.renderSearchFilter)}
                    onDropdownSelect={this.onFilterChange} />

                {this.renderInput()}
            </React.Fragment>
        )
    }

    // Render dropdown option for the search field
    renderSearchFilter = (filter: ISearchFilter, index: number) => {
        const { searchField } = this.props;
        return {
            id: filter.id.toString(),
            display: filter.display,
            selected: searchField.filter.id === filter.id
        }
    }

    // Update the filter (and clear the query)
    onFilterChange = (id: string) => {
        const filterId = parseInt(id);
        const filter = this.props.searchFilters.filter(f => f.id === filterId)[0];
        let query = InitialValues.Text;
        if (filter.parameterType.typeString === FilterParameterTypeString.Number) {
            query = InitialValues.Number;
        } else if (filter.parameterType.typeString === FilterParameterTypeString.Date) {
            query = InitialValues.Date + moment().format('MM/DD/YYYY')
        }
        this.props.onChange({
            ...this.props.searchField,
            filter,
            query,
            valid: true
        })
    }

    // Render the input for the filter
    renderInput = () => {
        const {
            searchField,
            searchFilters,
            epiProps
        } = this.props;

        if (searchField.filter.parameterType.typeString === FilterParameterTypeString.String) {
            // On text searches, only display one text box

            return (
                <div className="advanced-search-field-input">
                    <input
                        type="text"
                        ref={input => this.initialInput = input}
                        className="form-element-input"
                        value={searchField.query}
                        placeholder={epiProps.searchHint}
                        onChange={this.onSearchChange}
                        onKeyDown={this.props.onKeyDown} />

                    {!searchField.valid &&
                        <span className="search-field-warning">
                            <svg className="search-field-warning-icon">
                                <use xlinkHref="#icon-error"></use>
                        </svg>
                        Please enter a valid {searchField.filter.display}
                        </span>
                    }
                </div>
            );

        } else {
            // Dates and numbers can cover a range, render two
            const queries = searchField.query.split('-');
            const fromId = `initial-from`;
            const toId = `initial-to`;

            return (
                <div className="adv-search-range-fields">
                    <div className="adv-search-range-field">
                        <label htmlFor={fromId}>From</label>
                        <input
                            type="text"
                            id={fromId}
                            className="form-element-input"
                            value={queries[0] || ''}
                            placeholder={epiProps.searchFromHint}
                            onChange={e => this.onDoubleSearchChange(e, 0)}
                            onKeyDown={this.props.onKeyDown} />
                    </div>
                    <div className="adv-search-range-field">
                        <label htmlFor={toId}>To</label>
                        <input
                            type="text"
                            id={toId}
                            className="form-element-input"
                            value={queries[1] || ''}
                            placeholder={epiProps.searchToHint}
                            onChange={e => this.onDoubleSearchChange(e, 1)}
                            onKeyDown={this.props.onKeyDown} />
                    </div>
                    <span className="search-field-warning">
                        <svg className="search-field-warning-icon">
                            <use xlinkHref="#icon-error"></use>
                        </svg>
                        Please enter a valid {searchField.filter.display}
                    </span>
                </div>
            );
        }
    }

    // Update the query for one text box
    onSearchChange = (e) => {
        this.props.onChange({ ...this.props.searchField, query: e.target.value })
    }

    // Update the query for two text boxes
    onDoubleSearchChange = (e, index) => {
        const queries = this.props.searchField.query.split('-');

        let query;
        if (!e.target.value && !queries[(index + 1) % 2]) {
            query = '';
        } else if (index === 0) {
            query = `${e.target.value}-${queries[1] || ''}`;
        } else {
            query = `${queries[0] || ''}-${e.target.value}`;
        }

        this.props.onChange({ ...this.props.searchField, query: query })
    }
}